import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const WebSocketContext = createContext({
  socket: null,
  unreadCount: 0,
  setUnreadCount: () => {},
  liveNotification: null,
  resetLiveNotification: () => {},
});

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const { user, setUser, token } = useAuth();
  const socketRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const intentionalCloseRef = useRef(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const [liveNotification, setLiveNotification] = useState(null);

  // Seed unread count from REST on login
  useEffect(() => {
    if (!user?.id || !token) {
      setUnreadCount(0);
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((d) => setUnreadCount(d.count ?? 0))
      .catch(() => {});
  }, [user?.id, token]);

  const resetLiveNotification = useCallback(() => {
    setLiveNotification(null);
  }, []);

  // connect() is extracted so it can be called both on mount and on reconnect
  const connect = useCallback(() => {
    if (!user?.id || !token) return;
    if (socketRef.current) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      console.error("❌ REACT_APP_BACKEND_URL missing");
      return;
    }

    const wsUrl = backendUrl
      .replace("https://", "wss://")
      .replace("http://", "ws://");

    const socket = new WebSocket(
      `${wsUrl}/ws/${user.id}?token=${encodeURIComponent(token)}`
    );
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("🟢 WS connected");
      reconnectAttemptRef.current = 0; // reset backoff on successful connect
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data?.type === "user_status_update" && data.payload) {
          setUser((prev) => ({ ...prev, ...data.payload }));
          return;
        }

        if (data?.type === "new_notification" && data.notification) {
          setUnreadCount((n) => n + 1);
          setLiveNotification(data.notification);
          return;
        }
      } catch (err) {
        console.error("❌ WS parse error", err);
      }
    };

    socket.onerror = (err) => console.error("❌ WS error", err);

    socket.onclose = (event) => {
      console.log("🔴 WS closed", event.code);
      socketRef.current = null;

      // Don't reconnect on intentional close (logout / unmount)
      if (intentionalCloseRef.current) return;
      // Don't reconnect on auth failure codes
      if (event.code === 4001 || event.code === 4003) return;

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
      reconnectAttemptRef.current += 1;

      console.log(`🔄 WS reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`);
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };
  }, [user?.id, token, setUser]);

  useEffect(() => {
    if (!user?.id || !token) return;

    intentionalCloseRef.current = false;
    reconnectAttemptRef.current = 0;
    connect();

    return () => {
      intentionalCloseRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [user?.id, token, connect]);

  return (
    <WebSocketContext.Provider
      value={{
        socket: socketRef.current,
        unreadCount,
        setUnreadCount,
        liveNotification,
        resetLiveNotification,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
